import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useUser } from '../../context/UserContext';
import { saveAttachments, invalidateAttachmentCache } from './useChatAttachments';
import type { UploadedFile } from './useChatAttachments';
import type { ChatMessage, ChatMessageUser } from './useChatMessages';
import { uniqueChannelName } from '../../lib/realtime';

const PAGE_SIZE = 50;

// Cache user data to avoid repeated lookups
const userCache = new Map<string, ChatMessageUser>();

async function fetchUserInfo(userId: string): Promise<ChatMessageUser> {
  const cached = userCache.get(userId);
  if (cached) return cached;

  const { data } = await supabase
    .from('users')
    .select('id, name, avatar, role')
    .eq('id', userId)
    .single();

  const userInfo: ChatMessageUser = data
    ? { id: data.id, name: data.name || 'Unknown', avatar: data.avatar || null, role: data.role || '' }
    : { id: userId, name: 'Unknown', avatar: null, role: '' };

  userCache.set(userId, userInfo);
  return userInfo;
}

export function useThreadMessages(threadId: string | null) {
  const { user } = useUser();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [rootMessage, setRootMessage] = useState<ChatMessage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const offsetRef = useRef(0);

  // Fetch thread messages and root message
  const fetchMessages = useCallback(async () => {
    if (!threadId) {
      setMessages([]);
      setRootMessage(null);
      return;
    }

    setLoading(true);
    setError(null);
    offsetRef.current = 0;

    try {
      // Fetch root message first
      const { data: rootData, error: rootError } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('id', threadId)
        .single();

      if (rootError) throw rootError;

      // Fetch root message user info
      const rootUserInfo = await fetchUserInfo(rootData.user_id);
      const enrichedRootMessage: ChatMessage = {
        ...rootData,
        metadata: rootData.metadata as ChatMessage['metadata'],
        user: rootUserInfo,
      };
      setRootMessage(enrichedRootMessage);

      // Fetch thread replies
      const { data: repliesData, error: repliesError } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('thread_id', threadId)
        .is('deleted_at', null)
        .order('created_at', { ascending: true })
        .range(0, PAGE_SIZE - 1);

      if (repliesError) throw repliesError;

      // Fetch user info for all reply messages
      const userIds = [...new Set((repliesData || []).map(m => m.user_id))];
      await Promise.all(userIds.map(id => fetchUserInfo(id)));

      const enrichedReplies: ChatMessage[] = (repliesData || []).map(msg => ({
        ...msg,
        metadata: msg.metadata as ChatMessage['metadata'],
        user: userCache.get(msg.user_id) || {
          id: msg.user_id,
          name: 'Unknown',
          avatar: null,
          role: '',
        },
      }));

      setMessages(enrichedReplies);
      setHasMore((repliesData || []).length === PAGE_SIZE);
      offsetRef.current = (repliesData || []).length;
    } catch (err) {
      console.error('Error fetching thread messages:', err);
      setError((err as Error).message || 'Failed to load thread messages');
    } finally {
      setLoading(false);
    }
  }, [threadId]);

  // Load more (older messages)
  const loadMore = useCallback(async () => {
    if (!threadId || !hasMore || loading) return;

    try {
      const { data, error: fetchError } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('thread_id', threadId)
        .is('deleted_at', null)
        .order('created_at', { ascending: true })
        .range(offsetRef.current, offsetRef.current + PAGE_SIZE - 1);

      if (fetchError) throw fetchError;

      const userIds = [...new Set((data || []).map(m => m.user_id))];
      await Promise.all(userIds.map(id => fetchUserInfo(id)));

      const enrichedMessages: ChatMessage[] = (data || []).map(msg => ({
        ...msg,
        metadata: msg.metadata as ChatMessage['metadata'],
        user: userCache.get(msg.user_id) || {
          id: msg.user_id,
          name: 'Unknown',
          avatar: null,
          role: '',
        },
      }));

      // Append older messages (chronological order for threads)
      setMessages(prev => [...prev, ...enrichedMessages]);
      setHasMore((data || []).length === PAGE_SIZE);
      offsetRef.current += (data || []).length;
    } catch (err) {
      console.error('Error loading more thread messages:', err);
    }
  }, [threadId, hasMore, loading]);

  // Send a reply to the thread
  const sendReply = useCallback(
    async (content: string, attachments?: UploadedFile[]) => {
      if (!threadId || !user) return;

      const hasText = content.trim().length > 0;
      const hasAttachments = attachments && attachments.length > 0;

      // Need either text or attachments
      if (!hasText && !hasAttachments) return;

      try {
        // Build metadata if we have attachments
        const metadata = hasAttachments
          ? {
              attachments: attachments.map((a) => ({
                file_name: a.fileName,
                file_type: a.fileType,
                file_size: a.fileSize,
                public_url: a.publicUrl,
                width_px: a.widthPx,
                height_px: a.heightPx,
              })),
            }
          : null;

        // Get channel_id from the root message
        if (!rootMessage) return;

        const { data, error: sendError } = await supabase
          .from('chat_messages')
          .insert({
            channel_id: rootMessage.channel_id,
            user_id: user.id,
            content: content.trim(),
            thread_id: threadId,
            ...(metadata ? { metadata } : {}),
          })
          .select('id')
          .single();

        if (sendError) throw sendError;

        // Insert attachments into chat_attachments table
        if (hasAttachments && data?.id) {
          try {
            await saveAttachments(data.id, attachments);
            invalidateAttachmentCache(data.id);
          } catch (attErr) {
            console.error('Error saving attachments (message was sent):', attErr);
          }
        }

        // The realtime subscription will handle adding the message to state
        // The trigger will handle updating reply counts and subscriptions
      } catch (err) {
        console.error('Error sending thread reply:', err);
        throw err;
      }
    },
    [threadId, user, rootMessage]
  );

  // Edit a message (optimistic update + persist)
  const editMessage = useCallback(
    async (messageId: string, newContent: string) => {
      if (!user) return;
      const now = new Date().toISOString();

      // Optimistic update for thread replies
      setMessages(prev =>
        prev.map(m =>
          m.id === messageId ? { ...m, content: newContent, edited_at: now } : m
        )
      );

      // Also update root message if it's being edited
      if (messageId === threadId) {
        setRootMessage(prev =>
          prev ? { ...prev, content: newContent, edited_at: now } : prev
        );
      }

      try {
        const { error: updateError } = await supabase
          .from('chat_messages')
          .update({ content: newContent, edited_at: now })
          .eq('id', messageId)
          .eq('user_id', user.id);

        if (updateError) throw updateError;
      } catch (err) {
        console.error('Error editing message:', err);
        // Revert optimistic update by refetching
        fetchMessages();
        throw err;
      }
    },
    [user, threadId, fetchMessages]
  );

  // Soft-delete a message (optimistic update + persist)
  const deleteMessage = useCallback(
    async (messageId: string) => {
      if (!user) return;
      const now = new Date().toISOString();

      // Optimistic update for thread replies
      setMessages(prev =>
        prev.map(m =>
          m.id === messageId ? { ...m, deleted_at: now } : m
        )
      );

      // Also update root message if it's being deleted
      if (messageId === threadId) {
        setRootMessage(prev =>
          prev ? { ...prev, deleted_at: now } : prev
        );
      }

      try {
        const { error: deleteError } = await supabase
          .from('chat_messages')
          .update({ deleted_at: now })
          .eq('id', messageId)
          .eq('user_id', user.id);

        if (deleteError) throw deleteError;
      } catch (err) {
        console.error('Error deleting message:', err);
        // Revert optimistic update by refetching
        fetchMessages();
        throw err;
      }
    },
    [user, threadId, fetchMessages]
  );

  // Fetch on thread change
  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Realtime subscription for new and updated messages in this thread
  useEffect(() => {
    if (!threadId) return;

    const subscription = supabase
      .channel(uniqueChannelName(`thread:${threadId}`))
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `thread_id=eq.${threadId}`,
        },
        async (payload) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const newMsg = payload.new as any;

          // Fetch user info if not cached
          const userInfo = await fetchUserInfo(newMsg.user_id);

          const enrichedMessage: ChatMessage = {
            ...newMsg,
            user: userInfo,
          };

          setMessages(prev => {
            // Avoid duplicates
            if (prev.some(m => m.id === enrichedMessage.id)) return prev;
            return [...prev, enrichedMessage];
          });

          // Update offset to account for new message
          offsetRef.current += 1;
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_messages',
          filter: `thread_id=eq.${threadId}`,
        },
        async (payload) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const updated = payload.new as any;

          setMessages(prev =>
            prev.map(m =>
              m.id === updated.id
                ? { ...m, content: updated.content, edited_at: updated.edited_at, deleted_at: updated.deleted_at }
                : m
            )
          );
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_messages',
          filter: `id=eq.${threadId}`,
        },
        async (payload) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const updated = payload.new as any;

          // Update root message if it's updated
          setRootMessage(prev =>
            prev ? { ...prev, content: updated.content, edited_at: updated.edited_at, deleted_at: updated.deleted_at, reply_count: updated.reply_count, last_reply_at: updated.last_reply_at } : prev
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [threadId]);

  return { messages, rootMessage, loading, error, loadMore, hasMore, sendReply, editMessage, deleteMessage };
}